import bluebird from 'bluebird'

import {
  CreateReducer,
  CreateAction,
} from '../../store/utils/store'

import {
  getProjectHandlers,
} from '../../store/utils/api'

import {
  KnowledgeBaseState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  PayloadSearch,
  PayloadUpdate,
} from '../../types/knowledgebase'

import {
  IRecord_KnowledgeBase,
  IRecord_KnowledgeBase_Core,
  IRecord_KnowledgeBase_Update,
  IMeta_ProjectConfig_App_Config_Knowledgebase_Settings,
  IMeta_ProjectConfig_App_Config_Knowledgebase_Type,
  IData_KnowledgeBase_Section_Summary,
} from '@kaidam/fc-shared/src/types/schema/knowledgebase'

import {
  IPubsub_Message_Type_Knowledgebase_Article_Event,
  IPubsub_Message_Body_Knowledgebase_Article_Event,
} from '@kaidam/fc-shared/src/types/pubsub'

import {
  IElasticSearchResult,
} from '@kaidam/fc-shared/src/types/elasticsearch'

import {
  IElasticSearch_Source_Knowledgebase,
} from '@kaidam/fc-shared/src/types/knowledgebase'

import {
  getSocketClientFactory,
} from '../../store/utils/websocket'

import {
  knowledgebaseArticleChannel,
} from '@kaidam/fc-shared/src/pubsub/channels'


import {
  NetworkThunk,
} from '../utils/network'

import routerSelectors from '../selectors/router'
import projectActions from './project'
import projectSelectors from '../selectors/project'
import knowledgebaseSelectors from '../selectors/knowledgebase'
import snackbarActions from './snackbar'

const getArticleUpdateSocket = getSocketClientFactory<
  IPubsub_Message_Type_Knowledgebase_Article_Event,
  IPubsub_Message_Body_Knowledgebase_Article_Event
>()

const prefix = 'knowledgebase'

/*

  initial state

*/
const initialState: KnowledgeBaseState = {
  data: [],
  summary: [],
  allData: {},
  searchResults: [],
}

/*

  reducers

*/
const setData: ReducerHandler<KnowledgeBaseState, IRecord_KnowledgeBase[]> = (state, action) => {
  state.data = action.payload
}

const setArticle: ReducerHandler<KnowledgeBaseState, IRecord_KnowledgeBase> = (state, action) => {
  state.article = action.payload
}

const setSummary: ReducerHandler<KnowledgeBaseState, IData_KnowledgeBase_Section_Summary[]> = (state, action) => {
  state.summary = action.payload
}

const setAllData: ReducerHandler<KnowledgeBaseState, Record<string, IRecord_KnowledgeBase[]>> = (state, action) => {
  state.allData = action.payload
}

const setSearchResults: ReducerHandler<KnowledgeBaseState, IElasticSearchResult<IElasticSearch_Source_Knowledgebase>[]> = (state, action) => {
  state.searchResults = action.payload
}

/*

  thunks

*/
const list: ThunkHandler<string, void> = (type) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_KnowledgeBase[]>(`/knowledgebase/list/${orgId}/${type}`)
  dispatch(actions.setData(data))
}

const loadArticle: ThunkHandler<string, void> = (id) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_KnowledgeBase>(`/knowledgebase/${orgId}/${id}`)
  dispatch(actions.setArticle(data))
}

const listAll: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const typeConfigs = knowledgebaseSelectors.typeConfigs(getState())
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const typeDataArray = await bluebird.map(typeConfigs, async (type) => {
    const data = await handlers.get<IRecord_KnowledgeBase[]>(`/knowledgebase/list/${orgId}/${type.name}`)
    return {
      type: type.name,
      data,
    }
  })
  const allData = typeDataArray.reduce((all, typeData) => {
    all[typeData.type] = typeData.data
    return all
  }, {})
  dispatch(actions.setAllData(allData))
}

const loadSummary: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IData_KnowledgeBase_Section_Summary[]>(`/knowledgebase/summary/${orgId}`)
  dispatch(actions.setSummary(data))
}

const search: ThunkHandler<PayloadSearch, void> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IElasticSearchResult<IElasticSearch_Source_Knowledgebase>[]>(`/knowledgebase/search/${orgId}/${payload.type}`, {
    params: {
      query: payload.query,
    }
  })
  dispatch(actions.setSearchResults(data))
}

const get: ThunkHandler<string, IRecord_KnowledgeBase> = (id) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.get<IRecord_KnowledgeBase>(`/knowledgebase/${orgId}/${id}`)
  return data
}

const create: ThunkHandler<IRecord_KnowledgeBase_Core, boolean> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.post<IRecord_KnowledgeBase_Core, IRecord_KnowledgeBase>(`/knowledgebase/${orgId}`, payload)
  await dispatch(actions.loadSummary())
  dispatch(snackbarActions.setSuccess(`knowledgebase entry created`))
  return true
}

const update: ThunkHandler<PayloadUpdate, boolean> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const result = await handlers.put<IRecord_KnowledgeBase_Update, IRecord_KnowledgeBase>(`/knowledgebase/${orgId}/${payload.id}`, payload.data)
  await dispatch(actions.loadSummary())
  dispatch(snackbarActions.setSuccess(`knowledgebase entry updated`))
  return true
}

const addSection: ThunkHandler<{
  data: IMeta_ProjectConfig_App_Config_Knowledgebase_Type,
}, boolean> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.post<IMeta_ProjectConfig_App_Config_Knowledgebase_Type, void>(`/knowledgebase/${orgId}/section`, payload.data)
  await dispatch(actions.loadSummary())
  dispatch(snackbarActions.setSuccess(`knowledgebase section added`))
  return true
}

const updateSection: ThunkHandler<{
  type: string,
  data: IMeta_ProjectConfig_App_Config_Knowledgebase_Type,
}, boolean> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.put<IMeta_ProjectConfig_App_Config_Knowledgebase_Type, void>(`/knowledgebase/${orgId}/section/${payload.type}`, payload.data)
  await dispatch(actions.loadSummary())
  dispatch(snackbarActions.setSuccess(`knowledgebase section updated`))
  return true
}

const deleteSection: ThunkHandler<{
  type: string,
}, boolean> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  await handlers.delete(`/knowledgebase/${orgId}/section/${payload.type}`)
  await dispatch(actions.loadSummary())
  dispatch(snackbarActions.setSuccess(`knowledgebase section deleted`))
  return true
}

const del: ThunkHandler<string, boolean> = (payload) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const result = await handlers.delete<IRecord_KnowledgeBase>(`/knowledgebase/${orgId}/${payload}`)
  await dispatch(actions.loadSummary())
  dispatch(snackbarActions.setSuccess(`knowledgebase entry deleted`))
  return true
}

/*

  websockets

*/
const subscribeToArticleUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = knowledgebaseArticleChannel.wildcard(project)
  const socket = getArticleUpdateSocket()
  await socket.subscribe(channel, async (message) => {
    await dispatch(actions.loadSummary())
  })
  return true
}

const unsubscribeToArticleUpdates: ThunkHandler<void, boolean> = () => async (dispatch, getState) => {
  const {project} = projectSelectors.projectInfo(getState())
  if(!project) return false
  const channel = knowledgebaseArticleChannel.wildcard(project)
  const socket = getArticleUpdateSocket()
  socket.unsubscribe(channel)
  return true
}

/*

  proxy token

*/

export interface IProxyTokenResponse {
  token: string,
  url: string, 
}
const getProxyToken: ThunkHandler<string, IProxyTokenResponse> = (type: string) => async (dispatch, getState) => {
  const orgId = projectSelectors.currentOrgId(getState())
  const handlers = getProjectHandlers(getState)
  const data = await handlers.post<undefined, IProxyTokenResponse>(`/knowledgebase/proxyToken/${orgId}/${type}`)
  return data
}

/*

  collections

*/
const reducers = {
  setData,
  setArticle,
  setSummary,
  setAllData,
  setSearchResults,
}

export const actions = {
  setData: CreateAction(prefix, `setData`, setData),
  setArticle: CreateAction(prefix, `setArticle`, setArticle),
  setSummary: CreateAction(prefix, `setSummary`, setSummary),
  setAllData: CreateAction(prefix, `setAllData`, setAllData),
  setSearchResults: CreateAction(prefix, `setSearchResults`, setSearchResults),
  list: NetworkThunk(prefix, 'list', list, {
    snackbar: true,
  }),
  loadArticle: NetworkThunk(prefix, 'loadArticle', loadArticle, {
    snackbar: true,
  }),
  loadSummary: NetworkThunk(prefix, 'loadSummary', loadSummary, {
    snackbar: true,
  }),
  listAll: NetworkThunk(prefix, 'listAll', listAll, {
    snackbar: true,
  }),
  get: NetworkThunk(prefix, 'get', get, {
    snackbar: true,
  }),
  search: NetworkThunk(prefix, 'search', search, {
    snackbar: true,
  }),
  create: NetworkThunk(prefix, 'create', create, {
    snackbar: true,
    globalLoading: {
      message: 'creating item...'
    },
  }),
  update: NetworkThunk(prefix, 'update', update, {
    snackbar: true,
    globalLoading: {
      message: 'updating item...'
    },
  }),
  addSection: NetworkThunk(prefix, 'addSection', addSection, {
    snackbar: true,
    globalLoading: {
      message: 'adding section...'
    },
  }),
  updateSection: NetworkThunk(prefix, 'updateSection', updateSection, {
    snackbar: true,
    globalLoading: {
      message: 'updating section...'
    },
  }),
  deleteSection: NetworkThunk(prefix, 'deleteSection', deleteSection, {
    snackbar: true,
    globalLoading: {
      message: 'deleting section...'
    },
  }),
  delete: NetworkThunk(prefix, 'delete', del, {
    snackbar: true,
    globalLoading: {
      message: 'deleting item...'
    },
  }),
  subscribeToArticleUpdates: NetworkThunk(prefix, 'subscribeToArticleUpdates', subscribeToArticleUpdates, {
    snackbar: true,
  }),
  unsubscribeToArticleUpdates: NetworkThunk(prefix, 'unsubscribeToArticleUpdates', unsubscribeToArticleUpdates, {
    snackbar: true,
  }),
  getProxyToken: NetworkThunk(prefix, 'getProxyToken', getProxyToken, {
    snackbar: true,
  }),
}

export const reducer = CreateReducer<KnowledgeBaseState>({
  prefix,
  initialState,
  reducers,
})

export default actions